import { useProfitAndLoss } from '../../hooks/useReports';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, DollarSign, PieChart, FileSpreadsheet, Printer } from 'lucide-react';

export function ProfitAndLoss({ startDate, endDate }: { startDate: string; endDate: string }) {
  const { data, isLoading, error } = useProfitAndLoss(startDate, endDate);

  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        <Skeleton className="h-28 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 text-center text-rose-600 bg-rose-50 dark:bg-rose-950/30 rounded-2xl border border-rose-200 dark:border-rose-900 text-sm font-medium">
        Failed to calculate Profit & Loss Statement for the selected date range.
      </div>
    );
  }

  const formatCurrency = (val: number) =>
    `₹${(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const totalIncome = data.totalIncome || 0;
  const totalExpense = data.totalExpense || 0;
  const netProfit = data.netProfit || 0;
  const isProfitable = netProfit >= 0;

  // Margin %
  const profitMarginPct = totalIncome > 0 ? ((netProfit / totalIncome) * 100).toFixed(1) : '0.0';

  const exportCSV = () => {
    const csvRows = [
      ['Profit & Loss Statement', `Period: ${startDate} to ${endDate}`],
      [''],
      ['INCOME ACCOUNT', 'CODE', 'AMOUNT (INR)', '% OF TOTAL INCOME'],
      ...data.income.map(i => [i.name, i.code, i.netBalance, totalIncome > 0 ? ((i.netBalance / totalIncome) * 100).toFixed(1) + '%' : '0%']),
      ['Total Income', '', totalIncome, '100%'],
      [''],
      ['EXPENSE ACCOUNT', 'CODE', 'AMOUNT (INR)', '% OF TOTAL EXPENSES'],
      ...data.expenses.map(e => [e.name, e.code, e.netBalance, totalExpense > 0 ? ((e.netBalance / totalExpense) * 100).toFixed(1) + '%' : '0%']),
      ['Total Expenses', '', totalExpense, '100%'],
      [''],
      ['NET PROFIT / LOSS', '', netProfit, `${profitMarginPct}% Net Margin`],
    ];

    const csvContent = csvRows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Profit_and_Loss_${startDate}_to_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* ── Top Statement Summary Bar ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Total Income KPI Card */}
        <div className="bg-emerald-50 dark:from-emerald-950/40 dark:to-slate-900 p-4 rounded-2xl border border-emerald-200/80 dark:border-emerald-900/50 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-md shadow-emerald-600/20 shrink-0">
            <ArrowUpRight className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 uppercase tracking-wider">
              Total Revenue / Income
            </span>
            <p className="text-xl font-black text-emerald-900 dark:text-emerald-100 font-mono mt-0.5">
              {formatCurrency(totalIncome)}
            </p>
            <p className="text-[11px] text-emerald-700 dark:text-emerald-400 font-medium mt-0.5">
              {data.income.length} Income stream(s)
            </p>
          </div>
        </div>

        {/* Total Expense KPI Card */}
        <div className="bg-rose-50 dark:from-rose-950/40 dark:to-slate-900 p-4 rounded-2xl border border-rose-200/80 dark:border-rose-900/50 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-rose-600 text-white flex items-center justify-center shadow-md shadow-rose-600/20 shrink-0">
            <ArrowDownRight className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xs font-semibold text-rose-800 dark:text-rose-300 uppercase tracking-wider">
              Total Expenses
            </span>
            <p className="text-xl font-black text-rose-900 dark:text-rose-100 font-mono mt-0.5">
              {formatCurrency(totalExpense)}
            </p>
            <p className="text-[11px] text-rose-700 dark:text-rose-400 font-medium mt-0.5">
              {data.expenses.length} Expense line(s)
            </p>
          </div>
        </div>

        {/* Net Profit / Margin KPI Card */}
        <div
          className={`p-4 rounded-2xl border shadow-sm flex items-center justify-between gap-4 ${
            isProfitable
              ? 'bg-indigo-900 text-white border-indigo-500/30'
              : 'bg-rose-900 text-white border-rose-500/30'
          }`}
        >
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-300">
              <PieChart className="w-4 h-4 text-indigo-400" />
              <span>Net Profit Margin</span>
            </div>
            <p className="text-2xl font-black font-mono mt-1">
              {formatCurrency(netProfit)}
            </p>
            <p className="text-xs font-medium text-slate-300 mt-0.5">
              Margin: <span className="font-bold text-white">{profitMarginPct}%</span>
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button
              onClick={exportCSV}
              className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors text-xs font-medium flex items-center gap-1.5 border border-white/15"
              title="Export Statement to CSV"
            >
              <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
              <span className="hidden sm:inline">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Main Income & Expense Tables ────────────────────────────────────── */}
      <div className="bg-white dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden divide-y divide-slate-100 dark:divide-slate-900">
        {/* Header Bar */}
        <div className="px-6 py-4 bg-slate-50/70 dark:bg-slate-900/60 flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-indigo-600" />
            <span>Income & Expense Breakdown</span>
          </h3>
          <span className="text-xs text-slate-500 font-medium">
            Period: <span className="text-slate-800 dark:text-slate-200 font-semibold">{startDate}</span> to <span className="text-slate-800 dark:text-slate-200 font-semibold">{endDate}</span>
          </span>
        </div>

        {/* 1. Operating Income Section */}
        <div className="p-6 space-y-3">
          <div className="flex items-center justify-between pb-2 border-b border-emerald-100 dark:border-emerald-950">
            <h4 className="text-xs font-extrabold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              Operating Income & Revenue
            </h4>
            <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400 font-mono">
              Total: {formatCurrency(totalIncome)}
            </span>
          </div>

          <div className="space-y-1.5">
            {data.income.map((b) => {
              const rowPct = totalIncome > 0 ? Math.round((b.netBalance / totalIncome) * 100) : 0;
              return (
                <div
                  key={b.accountId}
                  className="group flex items-center justify-between p-3 rounded-xl bg-slate-50/60 dark:bg-slate-900/40 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20 border border-slate-100 dark:border-slate-800/80 transition-all text-xs"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[11px] px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-semibold">
                      {b.code}
                    </span>
                    <span className="font-bold text-slate-800 dark:text-slate-200">{b.name}</span>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="hidden md:flex items-center gap-2 w-32">
                      <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-emerald-500 h-full rounded-full transition-all duration-500"
                          style={{ width: `${rowPct}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono shrink-0 w-8">{rowPct}%</span>
                    </div>
                    <span className="font-mono font-bold text-emerald-700 dark:text-emerald-400 text-sm">
                      {formatCurrency(b.netBalance)}
                    </span>
                  </div>
                </div>
              );
            })}
            {data.income.length === 0 && (
              <div className="py-6 text-center text-xs text-slate-400 font-medium bg-slate-50 dark:bg-slate-900/30 rounded-xl">
                No revenue or income transactions recorded for this period.
              </div>
            )}
          </div>
        </div>

        {/* 2. Operating Expenses Section */}
        <div className="p-6 space-y-3">
          <div className="flex items-center justify-between pb-2 border-b border-rose-100 dark:border-rose-950">
            <h4 className="text-xs font-extrabold uppercase tracking-wider text-rose-700 dark:text-rose-400 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-rose-500" />
              Operating Expenses & Costs
            </h4>
            <span className="text-xs font-bold text-rose-700 dark:text-rose-400 font-mono">
              Total: {formatCurrency(totalExpense)}
            </span>
          </div>

          <div className="space-y-1.5">
            {data.expenses.map((b) => {
              const rowPct = totalExpense > 0 ? Math.round((b.netBalance / totalExpense) * 100) : 0;
              return (
                <div
                  key={b.accountId}
                  className="group flex items-center justify-between p-3 rounded-xl bg-slate-50/60 dark:bg-slate-900/40 hover:bg-rose-50/50 dark:hover:bg-rose-950/20 border border-slate-100 dark:border-slate-800/80 transition-all text-xs"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-[11px] px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-semibold">
                      {b.code}
                    </span>
                    <span className="font-bold text-slate-800 dark:text-slate-200">{b.name}</span>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="hidden md:flex items-center gap-2 w-32">
                      <div className="w-full bg-slate-200 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-rose-500 h-full rounded-full transition-all duration-500"
                          style={{ width: `${rowPct}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono shrink-0 w-8">{rowPct}%</span>
                    </div>
                    <span className="font-mono font-bold text-rose-700 dark:text-rose-400 text-sm">
                      {formatCurrency(b.netBalance)}
                    </span>
                  </div>
                </div>
              );
            })}
            {data.expenses.length === 0 && (
              <div className="py-6 text-center text-xs text-slate-400 font-medium bg-slate-50 dark:bg-slate-900/30 rounded-xl">
                No expense transactions recorded for this period.
              </div>
            )}
          </div>
        </div>

        {/* Hero Net Profit Footer Banner */}
        <div
          className={`p-6 flex flex-col sm:flex-row items-center justify-between gap-4 ${
            isProfitable
              ? 'bg-emerald-600 text-white'
              : 'bg-rose-600 text-white'
          }`}
        >
          <div className="flex items-center gap-3 text-center sm:text-left">
            <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md">
              {isProfitable ? <TrendingUp className="w-6 h-6" /> : <TrendingDown className="w-6 h-6" />}
            </div>
            <div>
              <span className="text-xs uppercase font-extrabold tracking-wider opacity-90">
                {isProfitable ? 'NET PROFIT FOR THE PERIOD' : 'NET LOSS FOR THE PERIOD'}
              </span>
              <p className="text-2xl sm:text-3xl font-black font-mono mt-0.5">
                {formatCurrency(netProfit)}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => window.print()}
              className="px-4 py-2 bg-white/15 hover:bg-white/25 text-white rounded-xl text-xs font-semibold backdrop-blur-md border border-white/20 transition-all flex items-center gap-1.5"
            >
              <Printer className="w-4 h-4" />
              <span>Print Statement</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
